<script setup lang="ts">
import type { DeepPartial } from '@apollo/client/utilities'
import FormChangePassword from '~/components/ui/FormChangePassword.vue'
import UploadAvatar from '~/components/ui/UploadAvatar.vue'
import SyncUser from '~/components/ui/SyncUser.vue'
import type { FormError } from '#ui/types'

import {
  GetSyncUserDocument,
  UpdateProfileDocument,
  UpdateProfilePictureDocument,
  type User
} from '~/apollo/gql/graphql'
import type { CorpsOption, Option, UnitKerjaOption } from '~/types/form'

type ProfileData = {
  id: number
  name: string
  nrp: string
  email: string
  phone: string
  ojsApiKey: string
  role: string
  gender: Option<string>
  angkatan: Option<number>
  pangkat: Option<number>
  corps: CorpsOption
  unit_kerja: UnitKerjaOption
  unit_organisasi: Option<string>
  jabatan: Option<string>
  jabatan_kepala: Option<string>
  satker: UnitKerjaOption
  sub_satker: UnitKerjaOption
  unit_kerja_type: number
  unit_organisasi_type: number
  jabatan_type: EJabatanType
  unit_kerja_custom: string
  unit_organisasi_custom: string
  jabatan_custom: string
}

const emit = defineEmits(['close', 'success'])

const tabItems = [
  {
    slot: 'profile',
    label: 'Profile'
  },
  {
    slot: 'password',
    label: 'Password'
  },
  {
    slot: 'sync',
    label: 'Sync'
  }
]

const { loggedUser } = storeToRefs(useAuthStore())
const syncUserRef = ref<InstanceType<typeof SyncUser>>()
const uploadAvatarRef = ref<InstanceType<typeof UploadAvatar>>()
const initialSync = ref(true)
const profile = ref<DeepPartial<User>>()
const selectedTab = ref(0)
const isUploadAvatarOpen = ref(false)
const loadingSyncUser = ref(false)
const loadingUploadAvatar = ref(false)
const isSyncUser = ref(false)

const state = ref<ProfileData>({
  id: null,
  name: '',
  nrp: '',
  role: 'user',
  email: '',
  phone: '',
  ojsApiKey: '',
  gender: null,
  angkatan: null,
  pangkat: null,
  corps: null,
  unit_kerja: null,
  unit_organisasi: null,
  jabatan: null,
  jabatan_kepala: null,
  satker: null,
  sub_satker: null,
  unit_kerja_type: 1,
  unit_organisasi_type: 1,
  jabatan_type: EJabatanType.FUNGSIONAL,
  unit_kerja_custom: null,
  unit_organisasi_custom: null,
  jabatan_custom: null
})

const { add: addToast } = useToast()
const { mutate: mutateUpdateUser, loading: loadingUpdateUser } = useMutation(UpdateProfileDocument)
const { mutate: mutateUploadAvatar } = useMutation(UpdateProfilePictureDocument)

const { isTni, loadOptions } = usePersonelOptions(state)

const {
  result: syncResult,
  load: loadSync,
  refetch: refetchSync
} = useLazyQuery(GetSyncUserDocument, {}, { fetchPolicy: 'no-cache' })

const fetchSync = async () => {
  if (initialSync.value) {
    initialSync.value = false
    await loadSync(null, {}, { fetchPolicy: 'no-cache' })
  } else {
    await refetchSync()
  }
}
const onClose = () => emit('close')
const onSuccess = () => emit('success', { description: 'Berhasil menyimpan perubahan' })
const onStartEdit = async () => {
  syncUserRef.value?.initEdit(state.value)
  selectedTab.value = 2
}
const onStartSync = async () => {
  loadingSyncUser.value = true
  isSyncUser.value = true

  try {
    await fetchSync()

    const {
      name,
      nrp,
      gender,
      angkatan,
      pangkat,
      corps,
      unit_kerja,
      unit_organisasi,
      jabatan,
      jabatan_kepala,
      jabatan_type,
      unit_kerja_custom,
      unit_organisasi_custom,
      jabatan_custom,
      satker,
      sub_satker
    } = syncResult.value.getSyncUser

    const afterSync: ProfileData = {
      id: state.value.id,
      name: name ?? '',
      nrp: nrp ?? '',
      role: state.value.role,
      email: state.value.email,
      phone: state.value.phone,
      ojsApiKey: profile.value.ojs_api_key,
      gender: { label: gender === EGender.MALE ? 'Laki-laki' : 'Perempuan', value: gender },
      angkatan: { label: angkatan.name, value: angkatan.id },
      pangkat: pangkat ? { label: pangkat.name, value: pangkat.id } : null,
      corps: corps
        ? {
            label: corps.name,
            value: corps.id,
            abbr: corps.abbr,
            abbr_fem: corps.abbr_fem
          }
        : null,
      unit_kerja: unit_kerja ? { label: unit_kerja.hirarki, value: unit_kerja.kode } : null,
      unit_organisasi: unit_organisasi
        ? { label: unit_organisasi.name, value: unit_organisasi.kode }
        : null,
      satker: satker ? { label: satker.hirarki, value: satker.kode } : null,
      sub_satker: sub_satker ? { label: sub_satker.hirarki, value: sub_satker.kode } : null,
      jabatan: null,
      jabatan_kepala: null,
      unit_kerja_type: state.value.unit_kerja_type,
      unit_organisasi_type: state.value.unit_organisasi_type,
      jabatan_type: jabatan_type as EJabatanType,
      unit_kerja_custom,
      unit_organisasi_custom,
      jabatan_custom
    }

    if (jabatan_type === EJabatanType.KEPALA) {
      afterSync.jabatan = { label: jabatan_kepala.jabatan_name, value: jabatan_kepala.kode }
    } else if (jabatan_type === EJabatanType.FUNGSIONAL) {
      afterSync.jabatan = { label: jabatan.name, value: jabatan.kode }
    } else {
      afterSync.jabatan_custom = jabatan_custom
    }

    syncUserRef.value?.initSync(state.value, afterSync)
    selectedTab.value = 2
  } catch (error) {
    console.error(error)
    addToast({
      color: 'red',
      title: 'Gagal sinkronisasi user!',
      description: error.message
    })
  } finally {
    loadingSyncUser.value = false
  }
}
const onSubmitUpdate = async () => {
  try {
    await mutateUpdateUser({
      email: profile.value.email,
      phone: profile.value.phone,
      ojsApiKey: profile.value.ojs_api_key
    })

    const user = { ...profile.value }

    useAuthStore().setUser(user)
    onSuccess()
  } catch (error) {
    console.error(error)
    addToast({
      color: 'red',
      title: 'Gagal menyimpan perubahan!',
      description: 'Silahkan coba beberapa saat lagi.'
    })
  }
}
const onUploadAvatar = async () => {
  loadingUploadAvatar.value = true

  const { file, base } = await uploadAvatarRef.value.getAvatar()

  try {
    const { data } = await mutateUploadAvatar(
      { file },
      { context: { headers: { 'apollo-require-preflight': true } } }
    )

    const photo = data?.updateProfilePicture?.data ?? base

    profile.value.photo = photo
    profile.value.photo_url = photo
    useAuthStore().setUser({ ...profile.value })

    addToast({
      color: 'green',
      title: 'Berhasil mengunggah foto profil!',
      description: 'Foto profil Anda berhasil diperbarui'
    })
  } catch {
    addToast({
      color: 'red',
      title: 'Gagal mengunggah foto profil!',
      description: 'Silahkan coba lagi'
    })
  } finally {
    loadingUploadAvatar.value = false
    isUploadAvatarOpen.value = false
  }
}
const onPasswordSuccess = () => {
  emit('success', { description: 'Berhasil mengubah password' })
  selectedTab.value = 0
}
const onSyncClose = () => {
  emit('close')
  window.location.reload()
}

const profileValidate = (state: typeof profile.value): FormError[] => {
  const errors: FormError[] = []

  // --- Validasi email ---
  const email = String(state.email ?? '').trim()
  if (!email) {
    errors.push({ path: 'email', message: 'Email wajib diisi' })
  } else {
    if (email.length < 8) {
      errors.push({ path: 'email', message: 'Email minimal 8 karakter' })
    }
    if (email.length > 100) {
      errors.push({ path: 'email', message: 'Email maksimal 100 karakter' })
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      errors.push({ path: 'email', message: 'Format email tidak valid' })
    }
  }

  const phone = String(state.phone ?? '').trim()
  if (phone) {
    const phoneRegex = /^\+?[0-9]{9,15}$/
    if (!phoneRegex.test(phone)) {
      errors.push({ path: 'phone', message: 'Format no. telepon tidak valid' })
    }
  }

  return errors
}

onMounted(async () => {
  loadOptions()
  profile.value = Object.assign({}, loggedUser.value)
  state.value = Object.assign(state.value, mapPersonelData(loggedUser.value, state.value))
})
</script>

<template>
  <UModal>
    <UCard :ui="{ ring: '', divide: 'divide-y divide-gray-100 dark:divide-gray-800' }">
      <template #header>
        <div class="flex items-center justify-between">
          <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white">
            {{
              selectedTab === 0
                ? 'Profil Anda'
                : selectedTab === 1
                  ? 'Ubah Password'
                  : 'Perbarui Biodata'
            }}
          </h3>
          <UButton
            color="gray"
            variant="ghost"
            icon="i-heroicons-x-mark-20-solid"
            class="-my-1"
            @click="onClose"
          />
        </div>
      </template>
      <UTabs
        v-model:model-value="selectedTab"
        :items="tabItems"
        :ui="{ list: { base: 'hidden' } }"
        class="w-full"
      >
        <template #profile>
          <div>
            <div class="grid grid-cols-7">
              <div class="relative">
                <UAvatar
                  size="xl"
                  :src="profile.photo_url"
                  :alt="profile.name"
                />
                <UButton
                  variant="soft"
                  color="primary"
                  size="2xs"
                  class="absolute bottom-2 right-2 rounded-full px-1 shadow-sm shadow-slate-300 dark:shadow-slate-700"
                  @click="isUploadAvatarOpen = true"
                >
                  <UIcon name="i-heroicons-pencil-solid" />
                </UButton>

                <UModal v-model="isUploadAvatarOpen">
                  <UCard class="relative">
                    <template #header>
                      <div class="flex items-center justify-between">
                        <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                          Upload Foto Profil
                        </h3>
                        <UButton
                          color="gray"
                          variant="ghost"
                          icon="i-heroicons-x-mark-20-solid"
                          class="-my-1"
                          @click="isUploadAvatarOpen = false"
                        />
                      </div>
                    </template>

                    <div
                      v-if="loadingUploadAvatar"
                      class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white bg-opacity-25"
                    >
                      <UIcon
                        name="i-eos-icons-three-dots-loading"
                        class="text-9xl text-orange-500"
                      />
                    </div>

                    <UploadAvatar
                      ref="uploadAvatarRef"
                      :initial-avatar="profile.photo"
                    />

                    <template #footer>
                      <div class="flex justify-end space-x-3">
                        <UButton
                          color="black"
                          @click="isUploadAvatarOpen = false"
                        >
                          Batal
                        </UButton>
                        <UButton
                          color="primary"
                          @click="onUploadAvatar()"
                        >
                          Simpan
                        </UButton>
                      </div>
                    </template>
                  </UCard>
                </UModal>
              </div>

              <div class="col-span-4 ms-2">
                <h3 class="text-xl font-extrabold">{{ loggedUser.name }}</h3>
                <p class="text-sm font-medium text-gray-400">NRP: {{ loggedUser.nrp }}</p>
              </div>
              <div class="col-span-2 text-right">
                <UButton
                  size="xs"
                  class="mb-1"
                  @click="selectedTab = 1"
                >
                  <span>Ubah Password</span>
                </UButton>
              </div>
            </div>

            <UDivider class="mb-4 mt-5" />
            <UForm
              :state="profile"
              :validate="profileValidate"
              class="space-y-4"
              @submit="onSubmitUpdate"
            >
              <div class="grid grid-cols-3 gap-4">
                <h3 class="text-md font-semibold">Email</h3>
                <UFormGroup
                  name="email"
                  class="col-span-2"
                >
                  <UInput
                    v-model="profile.email"
                    icon="i-heroicons-envelope"
                    placeholder="you@example.com"
                  />
                </UFormGroup>
              </div>

              <UDivider class="my-4" />

              <div class="grid grid-cols-3 gap-4">
                <h3 class="text-md font-semibold">No. Telepon</h3>
                <UFormGroup
                  name="phone"
                  class="col-span-2"
                >
                  <UInput
                    v-model="profile.phone"
                    icon="i-heroicons-phone"
                    placeholder="089763718198"
                  />
                </UFormGroup>
              </div>

              <UDivider class="my-4" />

              <div class="grid grid-cols-3 gap-4">
                <h3 class="text-md font-semibold">Kode EJurnal</h3>
                <UFormGroup
                  name="ojs"
                  class="col-span-2"
                >
                  <UInput
                    v-model="profile.ojs_api_key"
                    icon="i-heroicons-key"
                    placeholder="xxxxxxxxxxxx"
                  />
                </UFormGroup>
              </div>

              <div class="my-4 text-right">
                <UButton
                  color="primary"
                  size="xs"
                  :loading="loadingUpdateUser"
                  type="submit"
                >
                  Simpan Data Kontak
                </UButton>
              </div>
            </UForm>

            <UDivider class="my-4" />

            <div class="grid grid-cols-3 gap-4">
              <h3 class="text-md font-semibold">Angkatan</h3>
              <span class="col-span-2">{{ state.angkatan?.label || '-' }}</span>
            </div>

            <UDivider class="my-4" />

            <div class="grid grid-cols-3 gap-4">
              <h3 class="text-md font-semibold">{{ isTni ? 'Pangkat' : 'Golongan' }}</h3>
              <span class="col-span-2">{{ state.pangkat?.label || '-' }}</span>
            </div>

            <UDivider class="my-4" />

            <template v-if="isTni">
              <div class="grid grid-cols-3 gap-4">
                <h3 class="text-md font-semibold">Corps</h3>
                <span
                  v-if="state.corps"
                  class="col-span-2"
                >
                  {{ state.corps?.label }}
                  <span class="ms-3 font-semibold text-gray-500">
                    {{
                      state.gender?.value === EGender.MALE
                        ? state.corps?.abbr
                        : state.corps?.abbr_fem
                    }}
                  </span>
                </span>
                <span v-else>-</span>
              </div>

              <UDivider class="my-4" />
            </template>

            <div class="grid grid-cols-3 gap-4">
              <h3 class="text-md font-semibold">Unit Kerja</h3>
              <span
                v-if="state.unit_kerja_type === EUnitKerjaType.SIMPEG"
                class="col-span-2"
              >
                {{ state.unit_kerja?.label }}
              </span>
              <span
                v-else
                class="col-span-2"
              >
                {{ state.unit_kerja_custom ? state.unit_kerja_custom : '-' }}
              </span>
            </div>

            <UDivider class="my-4" />

            <div class="grid grid-cols-3 gap-4">
              <h3 class="text-md font-semibold">Jabatan</h3>
              <span
                v-if="state.jabatan_type !== EJabatanType.CUSTOM"
                class="col-span-2"
              >
                {{ state.jabatan?.label }}
              </span>
              <span
                v-else
                class="col-span-2"
              >
                {{ state.jabatan_custom ? state.jabatan_custom : '-' }}
              </span>
            </div>

            <UDivider class="my-4" />

            <div class="grid grid-cols-3 gap-4">
              <h3 class="text-md font-semibold">Unit Organisasi</h3>
              <span
                v-if="state.unit_organisasi_type === EUnitKerjaType.SIMPEG"
                class="col-span-2"
              >
                {{ state.unit_organisasi?.label }}
              </span>
              <span
                v-else
                class="col-span-2"
              >
                {{ state.unit_organisasi_custom ? state.unit_organisasi_custom : '-' }}
              </span>
            </div>
          </div>
        </template>

        <template #sync>
          <div>
            <SyncUser
              ref="syncUserRef"
              type="user"
              :is-sync="isSyncUser"
              @close="onSyncClose"
            />
          </div>
        </template>

        <template #password>
          <FormChangePassword
            class="-mt-3"
            @success="onPasswordSuccess"
          />
        </template>
      </UTabs>

      <template
        v-if="selectedTab === 0"
        #footer
      >
        <div
          class="flex flex-col items-stretch justify-end space-y-3 sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0"
        >
          <UTooltip text="Klik untuk meyinkronkan data dari SIMPEG">
            <UButton
              color="black"
              :loading="loadingSyncUser"
              @click="onStartSync"
            >
              Sinkronkan dari SIMPEG
            </UButton>
          </UTooltip>

          <UButton
            color="black"
            @click="onStartEdit"
          >
            Edit Biodata
          </UButton>
        </div>
      </template>
    </UCard>
  </UModal>
</template>
